import { Link } from 'react-router-dom';
import { ArrowRight, Check, X } from 'lucide-react';
import { SEO, orgJsonLd, softwareJsonLd, faqJsonLd } from '../components/SEO';

/**
 * Reusable SEO landing page shell for keyword-targeted pages.
 * Props: title, description, path, h1, subhead, sections[], comparison?, faqs[]
 */
export function SeoPageShell({ title, description, path, h1, subhead, sections, comparison, faqs, cta = 'Start 7-Day Free Trial' }) {
  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid={`seo-page-${path.replace(/\//g, '')}`}>
      <SEO
        title={title}
        description={description}
        path={path}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [orgJsonLd, softwareJsonLd, ...(faqs ? [faqJsonLd(faqs)] : [])],
        }}
      />

      {/* ===== HERO ===== */}
      <section className="pt-28 pb-14 md:pt-36 md:pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-[3.5rem] font-bold tracking-tight text-[#0A0A0A] leading-[1.1] mb-5">
            {h1}
          </h1>
          <p className="text-lg text-[#5C6B67] leading-relaxed mb-8 max-w-2xl">
            {subhead}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/register">
              <button className="bg-[#11302A] text-white px-8 py-4 rounded-full font-medium hover:bg-[#0D241F] hover:-translate-y-0.5 transition-all shadow-lg shadow-[#11302A]/20 flex items-center gap-2" data-testid="seo-hero-cta">
                {cta}
                <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <Link to="/pricing">
              <button className="bg-white text-[#11302A] px-8 py-4 rounded-full font-medium border border-[#E2E8E6] hover:bg-[#E5EBE9] transition-all">
                See Pricing
              </button>
            </Link>
          </div>
          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#5C6B67]">
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#00A859]" /> Starting $19/mo</span>
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#00A859]" /> 7-day free trial</span>
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#00A859]" /> No annual contract</span>
          </div>
        </div>
      </section>

      {/* ===== COMPARISON TABLE (optional) ===== */}
      {comparison && (
        <section className="py-14 bg-white border-y border-[#E2E8E6]">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-8 text-center">
              {comparison.title}
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full border border-[#E2E8E6] rounded-2xl overflow-hidden">
                <thead className="bg-[#FAFAFA]">
                  <tr>
                    <th className="p-4 text-left text-xs uppercase tracking-wider text-[#5C6B67] font-semibold">Feature</th>
                    {comparison.competitors.map((c) => (
                      <th key={c} className="p-4 text-left text-xs uppercase tracking-wider text-[#5C6B67] font-semibold">{c}</th>
                    ))}
                    <th className="p-4 text-left text-xs uppercase tracking-wider text-[#00A859] font-semibold bg-[#00A859]/5">RaiseRoot</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.rows.map((row, i) => (
                    <tr key={i} className="border-t border-[#E2E8E6]">
                      <td className="p-4 text-sm text-[#0A0A0A] font-medium">{row.feature}</td>
                      {row.competitors.map((val, j) => (
                        <td key={j} className="p-4 text-sm text-[#5C6B67]">
                          {val === true ? <Check className="w-4 h-4 text-[#00A859]" /> : val === false ? <X className="w-4 h-4 text-stone-300" /> : val}
                        </td>
                      ))}
                      <td className="p-4 text-sm text-[#0A0A0A] bg-[#00A859]/5">
                        {row.raiseroot === true ? <Check className="w-4 h-4 text-[#00A859]" /> : row.raiseroot === false ? <X className="w-4 h-4 text-stone-300" /> : row.raiseroot}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* ===== CONTENT SECTIONS ===== */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 space-y-14">
          {sections.map((sec, i) => (
            <div key={i}>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-4">
                {sec.h2}
              </h2>
              <div className="text-[#5C6B67] leading-relaxed space-y-4">
                {sec.paragraphs.map((p, j) => (
                  <p key={j} className="text-base">{p}</p>
                ))}
                {sec.bullets && (
                  <ul className="space-y-2 mt-4">
                    {sec.bullets.map((b, j) => (
                      <li key={j} className="flex items-start gap-2 text-[#0A0A0A]">
                        <Check className="w-4 h-4 text-[#00A859] flex-shrink-0 mt-0.5" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== FAQ ===== */}
      {faqs && faqs.length > 0 && (
        <section className="py-16 bg-white border-t border-[#E2E8E6]">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {faqs.map((f, i) => (
                <div key={i}>
                  <h3 className="font-semibold text-[#0A0A0A] mb-2">{f.q}</h3>
                  <p className="text-[#5C6B67] leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== FINAL CTA ===== */}
      <section className="py-16 bg-[#11302A] text-white">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
            Ready to try it?
          </h2>
          <p className="text-white/70 text-lg mb-8">
            Start your 7-day free trial. No credit card charged until day 8.
          </p>
          <Link to="/register">
            <button className="bg-[#00A859] text-white px-10 py-4 rounded-full font-medium hover:bg-[#008A4A] transition-all inline-flex items-center gap-2">
              {cta}
              <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}
